// ClintoBytes Copilot client (Phase 1 quotation G2; API contract §6ab).
import { api } from '@/lib/api/client';
import type { ReferenceAnswer } from '@/features/clinical-references/api';

/** Where a chip or card sends the user. One of module / admin / record_uhid is set. */
export interface CopilotLink {
  label?: string;
  module?: string;
  tab?: string;
  admin?: string;
  record_uhid?: string;
}

/** A table cell: plain text/number, or an amount the screen formats as rupees. */
export type CopilotCell = string | number | null | { money: number };

/** An over-time breakdown of a measure (query_metric with group_by day / week / month). */
export interface CopilotChart {
  kind: 'bar' | 'line';
  unit: 'count' | 'money' | 'minutes' | 'hours' | 'days' | 'percent';
  points: { label: string; value: number | null }[];
}

export interface CopilotCard {
  type: 'table';
  title: string;
  kpis?: { label: string; value: string | number; money?: boolean }[];
  columns?: string[];
  rows?: CopilotCell[][];
  links?: CopilotLink[];
  open?: CopilotLink;
  note?: string;
  chart?: CopilotChart;
}

export interface CopilotAction {
  kind: 'book_appointment';
  slot_id: number;
  patient_id: number;
  title: string;
  lines: string[];
  confirm_label: string;
}

export type CopilotPart =
  | { type: 'text'; text: string }
  | { type: 'data'; tool: string; card: CopilotCard }
  | ({ type: 'navigate' } & CopilotLink)
  | { type: 'help'; articles: { title: string; steps: string[]; navigate: CopilotLink | null }[] }
  | { type: 'clarify'; question: string; options: { label: string; value: string }[] }
  | { type: 'action'; action: CopilotAction }
  | { type: 'references'; result: ReferenceAnswer }
  | { type: 'refused'; label: string }
  | { type: 'handoff' }
  | { type: 'disclosure'; branch?: string; tools: { label: string; name: string; count: number | null; ok: boolean }[] };

export interface CopilotReply { session_id: number; turn_id: number; created_at: string; parts: CopilotPart[] }

export interface CopilotSessionSummary { id: number; title: string; created_at: string; last_at: string; turns: number }
export interface CopilotSession {
  id: number; title: string; created_at: string; last_at: string;
  turns: { id: number; question: string; parts: CopilotPart[]; rating: -1 | 1 | null; action_status: string; created_at: string }[];
}

export const fetchCopilotStatus = () => api.get<{ available: boolean; reason: string | null }>('/copilot/status');
export const askCopilot = (message: string, sessionId?: number | null) =>
  api.post<CopilotReply>('/copilot/chat', { message, session_id: sessionId ?? undefined });
export const fetchCopilotSessions = () => api.get<CopilotSessionSummary[]>('/copilot/sessions');
export const fetchCopilotSession = (id: number) => api.get<CopilotSession>(`/copilot/sessions/${id}`);
export const deleteCopilotSession = (id: number) => api.delete<{ deleted: true }>(`/copilot/sessions/${id}`);
export const rateCopilotTurn = (id: number, rating: 1 | -1) => api.post<{ ok: true }>(`/copilot/turns/${id}/feedback`, { rating });
export const reportCopilotAction = (id: number, status: 'confirmed' | 'cancelled') =>
  api.post<{ ok: true }>(`/copilot/turns/${id}/action-result`, { status });

/** Opens the Copilot window from anywhere (Ctrl+K "Ask Copilot", Alt+/); an optional question is asked straight away. */
export const OPEN_COPILOT_EVENT = 'hms:open-copilot';
export function openCopilot(question?: string) {
  window.dispatchEvent(new CustomEvent(OPEN_COPILOT_EVENT, { detail: { question } }));
}
